// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

/**
 * The org's own subcategory and cost-code catalog, and what the importer needs
 * to know about it.
 *
 * Every org owns an editable copy of the standard list, so two firms reading
 * the same estimate should get their own codes out of it, not CSI's. The
 * importer used to hardcode the built-in list, which meant a firm that had
 * carefully set up its codes imported a job and got the stock ones back — and
 * since the portfolio views group by cost code, their imported history and
 * their live work ended up in two taxonomies that would not roll up together.
 *
 * The fetch lives here rather than in the hook so the importer and the hook
 * read the catalog the same way instead of two ways that drift.
 */

import { supabase } from "./supabase";
import { DEFAULT_SUBS } from "../projectData";

export interface CostCodeEntry {
  id?: string;
  divId: string;
  costCode: string;
  name: string;
  sortOrder?: number;
}

/** Where a line goes when nothing in the catalog fits. */
export const UNASSIGNED_CODE = "(unassigned)";

export function builtInByDiv(): Record<string, CostCodeEntry[]> {
  const out: Record<string, CostCodeEntry[]> = {};
  Object.entries(DEFAULT_SUBS as Record<string, { name: string; costCode: string }[]>)
    .forEach(([divId, subs]) => {
      out[divId] = subs.map((s, i) => ({ divId, costCode: s.costCode, name: s.name, sortOrder: (i + 1) * 10 }));
    });
  return out;
}

export const BUILT_IN_BY_DIV = builtInByDiv();
export const BUILT_IN_FLAT = Object.values(BUILT_IN_BY_DIV).flat();

export interface CatalogResult {
  byDiv: Record<string, CostCodeEntry[]>;
  all: CostCodeEntry[];
  /** True when the rows came from the org table rather than the built-ins. */
  isOrgCatalog: boolean;
}

/**
 * Reads an org's catalog, falling back to the built-ins.
 *
 * An org with zero rows keeps an empty catalog on purpose: substituting the
 * built-ins would resurrect entries somebody deleted deliberately.
 */
export async function fetchOrgCostCodes(orgId: string | null | undefined): Promise<CatalogResult> {
  if (!orgId || !supabase) {
    return { byDiv: BUILT_IN_BY_DIV, all: BUILT_IN_FLAT, isOrgCatalog: false };
  }
  const { data, error } = await supabase
    .from("org_cost_codes")
    .select("id, div_id, cost_code, name, sort_order")
    .eq("org_id", orgId)
    .order("div_id")
    .order("sort_order");

  if (error) {
    console.warn("fetchOrgCostCodes:", error.message);
    return { byDiv: BUILT_IN_BY_DIV, all: BUILT_IN_FLAT, isOrgCatalog: false };
  }

  const all: CostCodeEntry[] = (data || []).map((r: any) => ({
    id: r.id, divId: r.div_id, costCode: r.cost_code, name: r.name, sortOrder: r.sort_order,
  }));
  const byDiv: Record<string, CostCodeEntry[]> = {};
  all.forEach((e) => { (byDiv[e.divId] ||= []).push(e); });
  return { byDiv, all, isOrgCatalog: true };
}

// ── What the importer is told ────────────────────────────────────────────────

/**
 * The catalog as a block the importer can be handed.
 *
 * Grouped by division and capped, because a catalog of several hundred entries
 * crowds out the instructions around it. The cap is generous enough that a
 * normal firm's list arrives whole.
 */
export function catalogForPrompt(entries: CostCodeEntry[], maxEntries = 400): string {
  const kept = entries.slice(0, maxEntries);
  const byDiv: Record<string, CostCodeEntry[]> = {};
  for (const e of kept) (byDiv[e.divId] ||= []).push(e);

  const lines: string[] = [];
  for (const divId of Object.keys(byDiv).sort((a, b) =>
    (parseInt(a.replace(/\D/g, ""), 10) || 0) - (parseInt(b.replace(/\D/g, ""), 10) || 0))) {
    for (const e of byDiv[divId]) {
      lines.push(`${divId}  ${e.costCode}  ${e.name}`);
    }
  }
  return lines.join("\n");
}

/** Codes the catalog actually contains, for checking what the importer returned. */
export function validCodeSet(entries: CostCodeEntry[]): Set<string> {
  return new Set(entries.map((e) => normalizeCostCode(e.costCode)).filter(Boolean));
}

export function normalizeCostCode(code: string): string {
  return String(code || "").replace(/[-.\s_]/g, "").trim().toLowerCase();
}

/**
 * Subcategories an import produced that are not in the catalog.
 *
 * Reported rather than corrected: a line the importer could not place is
 * visible and fixable, and one it placed by inventing a code looks right and
 * is not.
 */
export function unmappedSubcategories(
  state: any,
  valid: Set<string>,
): Array<{ name: string; costCode: string; lines: number }> {
  const out: Array<{ name: string; costCode: string; lines: number }> = [];
  for (const div of state?.divisions || []) {
    for (const sub of div.subcategories || []) {
      const code = normalizeCostCode(sub.costCode);
      if (code && valid.has(code)) continue;
      const lines = countRows(sub.rows);
      if (lines > 0) out.push({ name: sub.name || "(unnamed)", costCode: sub.costCode || "", lines });
    }
  }
  return out;
}

function countRows(rows: any[]): number {
  let n = 0;
  for (const r of rows || []) {
    if (r.type === "item" || (!r.type && r.id)) n++;
    if (r.children) n += countRows(r.children);
    if (r.rows) n += countRows(r.rows);
  }
  return n;
}
